import { type StudyRunCaseDashboardMatrixRowDto, StudyRunCaseDashboardMatrixStatus } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { Badge, type BadgeProps } from '@components/ui/badge';
import { type FC } from 'react';
import { InfoTooltip, TruncatedMetricText, formatNullableScore } from './meia-p4-dashboard-primitives.component';

type MeasurementMatrixPanelProps = {
  rows: StudyRunCaseDashboardMatrixRowDto[];
};

export const MeasurementMatrixPanel: FC<MeasurementMatrixPanelProps> = (props) => {
  const { rows } = props;

  return (
    <Tile
      className="overflow-hidden"
      contentClassName="gap-3"
      header={{
        title: (
          <span className="inline-flex items-center gap-1.5">
            Measurement Matrix
            <InfoTooltip>
              Traceability rows come from authored MoS, MoE, MoP links, tasks, and assigned technologies. Scores are
              overlaid only when the selected run case has stored output.
            </InfoTooltip>
          </span>
        ),
      }}
    >
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-left text-xs">
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr>
              <th className="px-3 py-2 font-medium text-gray-700">Stage</th>
              <th className="px-3 py-2 font-medium text-gray-700">Measure</th>
              <th className="px-3 py-2 font-medium text-gray-700">Targets</th>
              <th className="px-3 py-2 font-medium text-gray-700">Traceability</th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">Score</th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-8 text-center text-gray-500"
                  colSpan={6}
                >
                  No authored MoS, MoE, or MoP rows found for this thread.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  className="border-b border-gray-100 odd:bg-white even:bg-slate-50/70 last:border-0"
                  key={row.id}
                >
                  <td className="px-3 py-3 align-top">
                    <div className="font-medium text-gray-950">{row.stageName}</div>
                    <div className="font-mono text-[10px] text-gray-400">{row.stageId.slice(0, 8)}</div>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <div className="flex flex-col gap-1">
                      <Badge
                        className="w-fit"
                        size="xs"
                        variant={matrixMeasureVariant(row.measureType)}
                      >
                        {row.measureType}
                      </Badge>
                      <TruncatedMetricText
                        className="max-w-[280px] text-gray-800"
                        text={row.measureLabel}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top font-mono text-[10px] text-gray-500">
                    {matrixObjectiveText(row)}
                  </td>
                  <td className="px-3 py-3 align-top">
                    <div className="flex flex-col gap-1.5 text-gray-600">
                      <span>{formatMatrixTaskCount(row.tasks.length)}</span>
                      <span>{formatMatrixTechnologyCount(row.technologies.length)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center align-top font-mono font-semibold text-gray-900">
                    {formatNullableScore(row.score)}
                  </td>
                  <td className="px-3 py-3 text-center align-top">
                    <Badge
                      size="xs"
                      variant={matrixStatusVariant(row.status)}
                    >
                      {matrixStatusLabel(row.status)}
                    </Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Tile>
  );
};

const matrixObjectiveText = (row: StudyRunCaseDashboardMatrixRowDto): string => {
  const details = [
    row.threshold ? `Threshold ${row.threshold}` : null,
    row.objective ? `Objective ${row.objective}` : null,
  ].filter((detail): detail is string => Boolean(detail));

  return details.length === 0 ? '-' : details.join(' · ');
};

const formatMatrixTaskCount = (count: number): string => `${count} task${count === 1 ? '' : 's'}`;

const formatMatrixTechnologyCount = (count: number): string => `${count} technolog${count === 1 ? 'y' : 'ies'}`;

const matrixStatusLabel = (status: StudyRunCaseDashboardMatrixStatus): string => {
  switch (status) {
    case StudyRunCaseDashboardMatrixStatus.AT_RISK:
      return 'At risk';
    case StudyRunCaseDashboardMatrixStatus.COMPLETE:
      return 'Complete';
    case StudyRunCaseDashboardMatrixStatus.FAIL:
      return 'Fail';
    case StudyRunCaseDashboardMatrixStatus.GAP:
      return 'Gap';
    case StudyRunCaseDashboardMatrixStatus.NOT_SCORED:
      return 'Not scored';
    case StudyRunCaseDashboardMatrixStatus.PASS:
      return 'Pass';
  }
};

const matrixStatusVariant = (status: StudyRunCaseDashboardMatrixStatus): BadgeProps['variant'] => {
  switch (status) {
    case StudyRunCaseDashboardMatrixStatus.AT_RISK:
      return 'warning';
    case StudyRunCaseDashboardMatrixStatus.COMPLETE:
      return 'success';
    case StudyRunCaseDashboardMatrixStatus.FAIL:
      return 'destructive';
    case StudyRunCaseDashboardMatrixStatus.GAP:
      return 'destructive';
    case StudyRunCaseDashboardMatrixStatus.NOT_SCORED:
      return 'pending';
    case StudyRunCaseDashboardMatrixStatus.PASS:
      return 'success';
  }
};

const matrixMeasureVariant = (measureType: StudyRunCaseDashboardMatrixRowDto['measureType']): BadgeProps['variant'] => {
  switch (measureType) {
    case 'MOE':
      return 'warning';
    case 'MOP':
      return 'primary';
    case 'MOS':
      return 'success';
  }
};
